// The appearance preference as React state, and the one writer of
// `<html data-theme>` once the app has mounted (`public/theme-boot.js` writes it
// before first paint; this takes over and keeps it right).
//
// Design notes that keep the test suite green:
//   - The context default is System resolved to light, with a no-op setter, and
//     only the provider touches `<html>`. A component rendered WITHOUT a
//     `ThemeProvider` (most unit tests) therefore reads light and leaves the
//     document alone.
//   - The OS preference is read through `useSyncExternalStore`, so System
//     follows a live OS switch without a re-subscribe per render, and the
//     server snapshot (no `matchMedia`) is light.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import {
  applyResolvedTheme,
  readThemePreference,
  resolveTheme,
  subscribeSystemTheme,
  systemPrefersDark,
  THEME_STORAGE_KEY,
  writeThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from "./theme.js";

export interface ThemeContextValue {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
  readonly setPreference: (next: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  preference: "system",
  resolved: "light",
  setPreference: () => {},
});

const serverPrefersDark = (): boolean => false;

export function ThemeProvider({
  children,
  initial,
}: {
  readonly children: ReactNode;
  /** Override the stored preference (tests). */
  readonly initial?: ThemePreference;
}): JSX.Element {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => initial ?? readThemePreference(),
  );
  const systemDark = useSyncExternalStore(
    subscribeSystemTheme,
    systemPrefersDark,
    serverPrefersDark,
  );
  const resolved = resolveTheme(preference, systemDark);

  // Layout, not passive: the attribute must change in the same frame as the
  // render that chose it, or a toggle paints one frame in the old palette.
  useLayoutEffect(() => {
    applyResolvedTheme(resolved);
  }, [resolved]);

  // Another tab changed (or cleared) the stored choice: follow it. Re-read
  // rather than trust `newValue`, so the one parser decides what counts.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (event: StorageEvent): void => {
      if (event.key === THEME_STORAGE_KEY || event.key === null) {
        setPreferenceState(readThemePreference());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setPreference = useCallback((next: ThemePreference): void => {
    setPreferenceState(next);
    writeThemePreference(next);
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

/** The theme actually on screen — what a third-party widget with its own
 *  colour-mode switch (the markdown editor) must be told, never "auto". */
export function useResolvedTheme(): ResolvedTheme {
  return useContext(ThemeContext).resolved;
}
